import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';

const SPRING = { type: 'spring', stiffness: 420, damping: 22, mass: 0.7 };

/* ─────────────────────────────────────────────────────────────
   The padlock every door is drawn with.

   It is the one piece of the access screens that has to read from
   across a lobby, so it carries the state itself rather than leaving it
   to a chip: the shackle springs up and swings clear when a door
   unlocks, and drops back when it relocks.
   ───────────────────────────────────────────────────────────── */
export function LockGlyph({ open = false, size = 24 }) {
    const reduced = useReducedMotion();

    return (
        <svg className="acc-glyph" width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <motion.path
                d="M7.6 11V7.6a4.4 4.4 0 0 1 8.8 0V11"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.1"
                strokeLinecap="round"
                style={{ originX: 0.08, originY: 1 }}
                initial={false}
                animate={open ? { y: -2.4, rotate: -24 } : { y: 0, rotate: 0 }}
                transition={reduced ? { duration: 0 } : SPRING}
            />
            <rect x="4.8" y="10.4" width="14.4" height="10.4" rx="2.8" fill="currentColor" />
            <circle cx="12" cy="14.9" r="1.45" fill="rgba(0, 0, 0, 0.55)" />
            <rect x="11.35" y="15.3" width="1.3" height="2.7" rx="0.65" fill="rgba(0, 0, 0, 0.55)" />
        </svg>
    );
}

/* A ring that empties over `duration` seconds: the visible half of a
   momentary unlock. Keyed by the caller on the pulse it is counting. */
export function CountdownRing({ duration, size = 56, stroke = 3 }) {
    const reduced = useReducedMotion();
    const r = (size - stroke) / 2;

    return (
        <svg className="acc-ring" width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="currentColor" strokeOpacity="0.18" strokeWidth={stroke} />
            <motion.circle
                cx={size / 2}
                cy={size / 2}
                r={r}
                fill="none"
                stroke="currentColor"
                strokeWidth={stroke}
                strokeLinecap="round"
                transform={`rotate(-90 ${size / 2} ${size / 2})`}
                initial={{ pathLength: 1 }}
                animate={{ pathLength: 0 }}
                transition={{ duration: reduced ? 0 : duration, ease: 'linear' }}
            />
        </svg>
    );
}

/* Line icons, drawn on one 24px grid with one stroke so the feed, the
   cards and the buttons read as a set. */
const PATHS = {
    check: 'M5 12.5l4.3 4.3L19 7.1',
    cross: 'M6.5 6.5l11 11M17.5 6.5l-11 11',
    alert: 'M12 4 2.9 19.6h18.2L12 4zM12 10v4.2M12 17.1v.01',
    key: 'M10.6 12.9 19.5 4M16.2 7.3l2.4 2.4M13.9 9.6l1.8 1.8M11.5 16a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0z',
    phone: 'M8.5 2.8h7a1.7 1.7 0 0 1 1.7 1.7v15a1.7 1.7 0 0 1-1.7 1.7h-7a1.7 1.7 0 0 1-1.7-1.7v-15a1.7 1.7 0 0 1 1.7-1.7zM11 18.2h2',
    code: 'M9.5 4 7.5 20M16.5 4l-2 16M4.5 9h15M3.5 15h15',
    shield: 'M12 3.2 4.8 6v5.4c0 4.4 3 8.2 7.2 9.4 4.2-1.2 7.2-5 7.2-9.4V6L12 3.2z',
    shieldCheck: 'M12 3.2 4.8 6v5.4c0 4.4 3 8.2 7.2 9.4 4.2-1.2 7.2-5 7.2-9.4V6L12 3.2zM8.8 12.2l2.3 2.3 4.3-4.4',
    pause: 'M8.5 5.5v13M15.5 5.5v13',
    play: 'M8 5.5v13l10.5-6.5L8 5.5z',
    camera: 'M3.5 7.5h11v9h-11zM14.5 10.5l6-3.2v9.4l-6-3.2',
    offline: 'M2.5 8.6a14.5 14.5 0 0 1 19 0M5.8 12.1a9.6 9.6 0 0 1 12.4 0M9.2 15.5a4.6 4.6 0 0 1 5.6 0M12 19v.01M3.5 3.5l17 17',
    clock: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 7.5V12l3 2',
    door: 'M6.5 20.5V4.6a1.4 1.4 0 0 1 1.4-1.4h8.2a1.4 1.4 0 0 1 1.4 1.4v15.9M4 20.5h16M14.3 12.2v.01',
    search: 'M10.8 18a7.2 7.2 0 1 0 0-14.4 7.2 7.2 0 0 0 0 14.4zM16 16l4.5 4.5',
    share: 'M12 15V3.5M7.6 7.8 12 3.5l4.4 4.3M5 12.5v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6',
    refresh: 'M19.5 12a7.5 7.5 0 1 1-2.3-5.4M19.5 4v4.5H15',
    chevron: 'M9.5 6l6 6-6 6',
    person: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4.5 20.5a7.5 7.5 0 0 1 15 0',
    plus: 'M12 5v14M5 12h14',
    car: 'M5 16.5V12l1.8-4.6A1.8 1.8 0 0 1 8.5 6.2h7a1.8 1.8 0 0 1 1.7 1.2L19 12v4.5M5 12h14M5 16.5h14v2H5zM8 14.2v.01M16 14.2v.01',
    bolt: 'M13 3 5 13.5h6L10.5 21 19 10.5h-6L13 3z',
    eye: 'M2.5 12s3.5-6.5 9.5-6.5 9.5 6.5 9.5 6.5-3.5 6.5-9.5 6.5S2.5 12 2.5 12zM12 14.6a2.6 2.6 0 1 0 0-5.2 2.6 2.6 0 0 0 0 5.2z'
};

export function Icon({ name, size = 18, className = '' }) {
    return (
        <svg
            className={`acc-icon ${className}`.trim()}
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
        >
            <path d={PATHS[name] || PATHS.door} />
        </svg>
    );
}

/* The icon an event in the feed is drawn with. */
export function eventIcon(event) {
    if (event.kind === 'denied') return 'cross';
    if (event.label === 'Visitor' || /Visitor code/.test(event.via || '')) return 'code';
    if (event.label === 'Lockdown' || event.label === 'Override') return 'shield';
    if (event.label === 'Restored') return 'shieldCheck';
    if (event.label === 'Offline') return 'offline';
    if (event.kind === 'alarm') return event.tone === 'ok' ? 'check' : 'alert';
    if (event.label === 'Schedule') return 'clock';
    if (event.label === 'Relocked') return 'refresh';
    if (event.label === 'Credential') return 'key';
    if (/Mobile key/.test(event.via || '')) return 'phone';
    if (event.label === 'Unlocked') return 'door';
    if (event.door === 'garage-gate') return 'car';
    return event.kind === 'granted' ? 'check' : 'bolt';
}
